import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    fontFamily: "Helvetica",
    lineHeight: 1.3,
    color: "#1a1a1a",
    backgroundColor: "#ffffff",
  },
  header: {
    textAlign: "center",
    paddingVertical: 8,
    marginBottom: 15,
    backgroundColor: "#f8fafc",
    borderBottom: "2px solid #2563eb",
  },
  companyName: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#1e3a8a",
    marginBottom: 4,
  },
  address: {
    fontSize: 8,
    color: "#4b5563",
    marginBottom: 2,
  },
  title: {
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
    marginVertical: 12,
    color: "#1e3a8a",
    paddingBottom: 6,
    borderBottom: "1px solid #e5e7eb",
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#1e40af",
    marginBottom: 8,
    paddingBottom: 3,
    borderBottom: "1px solid #d1d5db",
  },
  section: {
    marginBottom: 12,
    padding: 10,
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    backgroundColor: "#fefefe",
  },
  questionRow: {
    flexDirection: "row",
    marginBottom: 8,
    gap: 8,
    flexWrap: "wrap",
  },
  questionGroup: {
    flex: 1,
    minWidth: "30%",
    marginBottom: 6,
  },
  fieldFull: {
    width: "100%",
    marginBottom: 6,
  },
  label: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#374151",
    marginBottom: 2,
  },
  field: {
    border: "1px solid #d1d5db",
    paddingVertical: 3,
    paddingHorizontal: 6,
    fontSize: 8,
    backgroundColor: "#ffffff",
    borderRadius: 2,
    minHeight: 18,
  },
  fieldValue: {
    fontSize: 8,
    paddingVertical: 3,
    paddingHorizontal: 6,
  },
  checkboxGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 15,
    marginTop: 4,
    marginBottom: 4,
  },
  checkboxItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  checkbox: {
    width: 10,
    height: 10,
    border: "1px solid #9ca3af",
    marginRight: 5,
    borderRadius: 1,
  },
  checkboxChecked: {
    width: 10,
    height: 10,
    border: "1px solid #2563eb",
    backgroundColor: "#2563eb",
    marginRight: 5,
    borderRadius: 1,
  },
  checkboxLabel: {
    fontSize: 8,
    color: "#374151",
  },
  table: {
    marginVertical: 6,
    border: "1px solid #d1d5db",
    borderRadius: 3,
    overflow: "hidden",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f9ff",
    borderBottom: "1px solid #2563eb",
    paddingVertical: 5,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #f3f4f6",
    paddingVertical: 4,
    minHeight: 22,
  },
  tableCell: {
    flex: 1,
    fontSize: 7,
    paddingHorizontal: 3,
    textAlign: "center",
  },
  signatureSection: {
    marginTop: 15,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: "1px solid #d1d5db",
    paddingTop: 12,
  },
  signatureBlock: {
    alignItems: "center",
    flex: 1,
  },
  signatureLine: {
    borderBottom: "1px solid #1a1a1a",
    width: 160,
    height: 1,
    marginBottom: 3,
  },
  signatureLabel: {
    fontSize: 7,
    color: "#6b7280",
    textAlign: "center",
  },
  paragraph: {
    fontSize: 8,
    marginBottom: 6,
    lineHeight: 1.3,
    textAlign: "left",
    color: "#374151",
  },
  disclaimer: {
    fontSize: 7,
    color: "#6b7280",
    fontStyle: "italic",
    marginTop: 12,
    textAlign: "center",
    borderTop: "1px solid #e5e7eb",
    paddingTop: 6,
  },
  required: {
    color: "#dc2626",
    fontSize: 8,
  },
  dutiesList: {
    marginLeft: 8,
    marginVertical: 6,
  },
  dutyItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 4,
  },
  bullet: {
    marginRight: 5,
    color: "#0096fa",
    fontSize: 8,
  },
  listText: {
    flex: 1,
    fontSize: 8,
  },
  fieldHalf: {
    flex: 1,
    marginBottom: 6,
  },
  cell: {
    border: "1px solid #e5e7eb",
    padding: 4,
    fontSize: 7,
    flex: 1,
    minWidth: "14%",
    maxWidth: "14%",
  },
  row: {
    flexDirection: "row",
    marginBottom: 6,
    gap: 8,
  },
  subTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#2563eb",
    marginBottom: 6,
  },
  highlightedSection: {
    marginBottom: 12,
    padding: 10,
    border: "1px solid #fecaca",
    borderRadius: 4,
    backgroundColor: "#fef2f2",
  },
  infoSection: {
    marginBottom: 12,
    padding: 10,
    border: "1px solid #bfdbfe",
    borderRadius: 4,
    backgroundColor: "#eff6ff",
  },
  note: {
    fontSize: 7,
    color: "#6b7280",
    marginTop: 4,
    fontStyle: "italic",
  },
});

const currentYear = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");

const PCAApplicationPDF = ({ formData }) => (
  <Document>
    {/* PAGE 1: Information Cover Sheet */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE, LLC</Text>
        <Text style={styles.address}>
          330 SW Fourth Street Suite #9B, Willmar MN 56201
        </Text>
        <Text style={styles.address}>
          Phone 320-455-2702 | Fax 320-272-2915
        </Text>
      </View>

      <Text style={styles.title}>INFORMATION COVER SHEET</Text>

      <View style={styles.section}>
        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Employee Name <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.employeeName}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Date of Birth <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.dob}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Gender <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.checkboxGroup}>
            <View style={styles.checkboxItem}>
              <View
                style={
                  formData.gender === "Male"
                    ? styles.checkboxChecked
                    : styles.checkbox
                }
              />
              <Text style={styles.label}>Male</Text>
            </View>
            <View style={styles.checkboxItem}>
              <View
                style={
                  formData.gender === "Female"
                    ? styles.checkboxChecked
                    : styles.checkbox
                }
              />
              <Text style={styles.label}>Female</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Address <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.address}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Employee Phone <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.employeePhone}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Email <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.email}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Are you currently working as PCA/CFSS?{" "}
            <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.checkboxGroup}>
            <View style={styles.checkboxItem}>
              <View
                style={
                  formData.currentlyWorkingPCA === "Yes"
                    ? styles.checkboxChecked
                    : styles.checkbox
                }
              />
              <Text style={styles.label}>Yes</Text>
            </View>
            <View style={styles.checkboxItem}>
              <View
                style={
                  formData.currentlyWorkingPCA === "No"
                    ? styles.checkboxChecked
                    : styles.checkbox
                }
              />
              <Text style={styles.label}>No</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            How many hours of PCA/CFSS are you currently working per week?
          </Text>
          <View style={styles.field}>
            <Text>{formData.currentHoursPerWeek}</Text>
          </View>
        </View>
      </View>

      <Text style={[styles.title, { marginTop: 20 }]}>
        ASSIGNED CLIENT INFORMATION
      </Text>

      <View style={styles.section}>
        <View style={styles.questionRow}>
          <Text style={styles.label}>Client Name</Text>
          <View style={styles.fieldFull}>
            <Text>{formData.clientName}</Text>
          </View>
        </View>
        <View style={styles.questionRow}>
          <Text style={styles.label}>Client Date Of Birth</Text>
          <View style={styles.fieldFull}>
            <Text>{formData.clientDOB}</Text>
          </View>
        </View>
        <View style={styles.questionRow}>
          <Text style={styles.label}>Client Address</Text>
          <View style={styles.fieldFull}>
            <Text>
              {formData.clientAddress} {formData.clientCity},{" "}
              {formData.clientState}, {formData.clientZip}
            </Text>
          </View>
        </View>
        <View style={styles.questionRow}>
          <Text style={styles.label}>Client or RP Phone Number</Text>
          <View style={styles.field}>
            <Text>{formData.clientPhone}</Text>
          </View>
        </View>
        <View style={styles.questionRow}>
          <Text style={styles.label}>Client Insurance Information</Text>
          <View style={styles.fieldFull}>
            <Text>{formData.clientInsurance}</Text>
          </View>
        </View>
        <View style={styles.questionRow}>
          <Text style={styles.label}>Any Additional Information</Text>
          <View style={styles.fieldFull}>
            <Text>{formData.additionalInfo}</Text>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        Disclaimer: All information provided must be accurate and complete.
        Falsification of information may result in termination of employment.
      </Text>
    </Page>

    {/* PAGE 2: Application for Employment - Personal Information */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE</Text>
        <Text style={styles.address}>P-320-455-2702 F-320-272-2915</Text>
      </View>

      <Text style={styles.title}>APPLICATION FOR EMPLOYMENT</Text>
      <Text
        style={{
          textAlign: "center",
          fontSize: 9,
          marginBottom: 15,
          color: "#0096fa",
        }}
      >
        Equal Opportunity Employer
      </Text>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Personal Information</Text>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Last Name <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.lastName}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              First Name <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.firstName}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Middle Initial</Text>
            <View style={styles.field}>
              <Text>{formData.middleInitial}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Social Security # <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.ssn}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Street Address <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.streetAddress}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              City/State <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.cityState}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Zip Code <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.zipCode}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Phone Number <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.phoneNumber}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            If hired, can you provide evidence of legal eligibility to work in
            the U.S.? <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.checkboxGroup}>
            <View style={styles.checkboxItem}>
              <View
                style={
                  formData.legalEligibility === "Yes"
                    ? styles.checkboxChecked
                    : styles.checkbox
                }
              />
              <Text style={styles.label}>Yes</Text>
            </View>
            <View style={styles.checkboxItem}>
              <View
                style={
                  formData.legalEligibility === "No"
                    ? styles.checkboxChecked
                    : styles.checkbox
                }
              />
              <Text style={styles.label}>No</Text>
            </View>
          </View>
        </View>

        <Text style={[styles.paragraph, { fontSize: 8, color: "#666" }]}>
          Any offer of employment is conditioned upon completing form I-9 and
          providing the appropriate documents for identity and valid work
          authorization.
        </Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Employment Details</Text>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Position Desired</Text>
            <View style={styles.field}>
              <Text>{formData.positionDesired}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Wage/Salary Desired</Text>
            <View style={styles.field}>
              <Text>{formData.wageSalaryDesired}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Employment Type <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.checkboxGroup}>
            <View style={styles.checkboxItem}>
              <View
                style={
                  formData.employmentType === "Part Time"
                    ? styles.checkboxChecked
                    : styles.checkbox
                }
              />
              <Text style={styles.label}>Part Time</Text>
            </View>
            <View style={styles.checkboxItem}>
              <View
                style={
                  formData.employmentType === "Full Time"
                    ? styles.checkboxChecked
                    : styles.checkbox
                }
              />
              <Text style={styles.label}>Full Time</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>When can you start?</Text>
            <View style={styles.field}>
              <Text>{formData.startDate}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Date of Birth</Text>
            <View style={styles.field}>
              <Text>{formData.dob}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Age</Text>
            <View style={styles.field}>
              <Text>{formData.age}</Text>
            </View>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        All information provided will be verified. Falsification may result in
        disqualification from employment.
      </Text>
    </Page>

    {/* PAGE 3: Application for Employment - Education & Availability */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE</Text>
        <Text style={styles.address}>P-320-455-2702 F-320-272-2915</Text>
      </View>

      <Text style={styles.title}>APPLICATION FOR EMPLOYMENT</Text>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Education History</Text>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Are you presently enrolled in school?{" "}
            <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.checkboxGroup}>
            <View style={styles.checkboxItem}>
              <View
                style={
                  formData.enrolledInSchool === "Yes"
                    ? styles.checkboxChecked
                    : styles.checkbox
                }
              />
              <Text style={styles.label}>Yes</Text>
            </View>
            <View style={styles.checkboxItem}>
              <View
                style={
                  formData.enrolledInSchool === "No"
                    ? styles.checkboxChecked
                    : styles.checkbox
                }
              />
              <Text style={styles.label}>No</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            If yes, give name & address of school and expected degree date:
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.schoolDetails}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            List of any job-related skills or accomplishments, including
            military service:
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.jobSkills}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Work Availability</Text>

        <Text style={[styles.label, { marginBottom: 10 }]}>
          What are the days and times you are available to work?
        </Text>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, { fontWeight: "bold" }]}>Day</Text>
            <Text style={[styles.tableCell, { fontWeight: "bold" }]}>From</Text>
            <Text style={[styles.tableCell, { fontWeight: "bold" }]}>To</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Monday</Text>
            <Text style={styles.tableCell}>{formData.mondayFrom}</Text>
            <Text style={styles.tableCell}>{formData.mondayTo}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Tuesday</Text>
            <Text style={styles.tableCell}>{formData.tuesdayFrom}</Text>
            <Text style={styles.tableCell}>{formData.tuesdayTo}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Wednesday</Text>
            <Text style={styles.tableCell}>{formData.wednesdayFrom}</Text>
            <Text style={styles.tableCell}>{formData.wednesdayTo}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Thursday</Text>
            <Text style={styles.tableCell}>{formData.thursdayFrom}</Text>
            <Text style={styles.tableCell}>{formData.thursdayTo}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Friday</Text>
            <Text style={styles.tableCell}>{formData.fridayFrom}</Text>
            <Text style={styles.tableCell}>{formData.fridayTo}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Saturday</Text>
            <Text style={styles.tableCell}>{formData.saturdayFrom}</Text>
            <Text style={styles.tableCell}>{formData.saturdayTo}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Sunday</Text>
            <Text style={styles.tableCell}>{formData.sundayFrom}</Text>
            <Text style={styles.tableCell}>{formData.sundayTo}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            How many hours per week are you available to work?
          </Text>
          <View style={styles.field}>
            <Text>{formData.availableHoursPerWeek}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Do you have any special requests or needs for a work schedule?
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.specialRequests}</Text>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        References will be contacted and verified. Please ensure all information
        is accurate.
      </Text>
    </Page>

    {/* PAGE 4: Professional References */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE</Text>
        <Text style={styles.address}>P-320-455-2702 F-320-272-2915</Text>
      </View>

      <Text style={styles.title}>PROFESSIONAL REFERENCES</Text>

      <View style={styles.section}>
        <Text style={[styles.label, { marginBottom: 10 }]}>
          Provide two references who are not related to you:
        </Text>

        <View
          style={[
            styles.section,
            { backgroundColor: "#f0f8ff", marginBottom: 10 },
          ]}
        >
          <Text style={[styles.sectionTitle, { color: "#0096fa" }]}>
            Reference 1
          </Text>

          <View style={styles.questionRow}>
            <View style={styles.questionGroup}>
              <Text style={styles.label}>Name and Occupation</Text>
              <View style={styles.field}>
                <Text>
                  {formData.ref1Name} - {formData.ref1Occupation}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.questionRow}>
            <View style={styles.questionGroup}>
              <Text style={styles.label}>
                How do you know them, and for how long?
              </Text>
              <View style={styles.field}>
                <Text>
                  {formData.ref1Relationship} - {formData.ref1HowLong}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.questionRow}>
            <View style={styles.questionGroup}>
              <Text style={styles.label}>Phone Number</Text>
              <View style={styles.field}>
                <Text>{formData.ref1Phone}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={[styles.section, { backgroundColor: "#f0f8ff" }]}>
          <Text style={[styles.sectionTitle, { color: "#0096fa" }]}>
            Reference 2
          </Text>

          <View style={styles.questionRow}>
            <View style={styles.questionGroup}>
              <Text style={styles.label}>Name and Occupation</Text>
              <View style={styles.field}>
                <Text>
                  {formData.ref2Name} - {formData.ref2Occupation}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.questionRow}>
            <View style={styles.questionGroup}>
              <Text style={styles.label}>
                How do you know them, and for how long?
              </Text>
              <View style={styles.field}>
                <Text>
                  {formData.ref2Relationship} - {formData.ref2HowLong}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.questionRow}>
            <View style={styles.questionGroup}>
              <Text style={styles.label}>Phone Number</Text>
              <View style={styles.field}>
                <Text>{formData.ref2Phone}</Text>
              </View>
            </View>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        References will be contacted and verified. Please ensure all information
        is accurate.
      </Text>
    </Page>

    {/* PAGE 5: Employment History */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE</Text>
        <Text style={styles.address}>P-320-455-2702 F-320-272-2915</Text>
      </View>

      <Text style={styles.title}>EMPLOYMENT HISTORY</Text>
      <Text style={{ textAlign: "center", fontSize: 9, marginBottom: 15 }}>
        List two most recent employers:
      </Text>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Previous Employer 1</Text>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Name of Employer</Text>
            <View style={styles.field}>
              <Text>{formData.employer1Name}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Your Job Title</Text>
            <View style={styles.field}>
              <Text>{formData.employer1Title}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>Address</Text>
          <View style={styles.fieldFull}>
            <Text>{formData.employer1Address}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>City, State, Zip Code</Text>
            <View style={styles.field}>
              <Text>{formData.employer1City}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Employer Phone #</Text>
            <View style={styles.field}>
              <Text>{formData.employer1Phone}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Dates of Employment From</Text>
            <View style={styles.field}>
              <Text>{formData.employer1DateFrom}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>To</Text>
            <View style={styles.field}>
              <Text>{formData.employer1DateTo}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Supervisor Name</Text>
            <View style={styles.field}>
              <Text>{formData.employer1Supervisor}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Reason for Leaving</Text>
            <View style={styles.field}>
              <Text>{formData.employer1Reason}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Previous Employer 2</Text>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Name of Employer</Text>
            <View style={styles.field}>
              <Text>{formData.employer2Name}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Your Job Title</Text>
            <View style={styles.field}>
              <Text>{formData.employer2Title}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>Address</Text>
          <View style={styles.fieldFull}>
            <Text>{formData.employer2Address}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>City, State, Zip Code</Text>
            <View style={styles.field}>
              <Text>{formData.employer2City}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Employer Phone #</Text>
            <View style={styles.field}>
              <Text>{formData.employer2Phone}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Dates of Employment From</Text>
            <View style={styles.field}>
              <Text>{formData.employer2DateFrom}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>To</Text>
            <View style={styles.field}>
              <Text>{formData.employer2DateTo}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Supervisor Name</Text>
            <View style={styles.field}>
              <Text>{formData.employer2Supervisor}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Reason for Leaving</Text>
            <View style={styles.field}>
              <Text>{formData.employer2Reason}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Certification and Authorization</Text>

        <Text style={styles.paragraph}>
          I certify that all the information provided in this employment
          application is true and complete to the best of my knowledge, and I
          authorize investigation of all statements contained in this
          application, including criminal background, employment history check,
          and drug test, as applicable. I understand that any false or
          incomplete information may disqualify me from further consideration
          for employment and may result in my immediate discharge if discovered
          later.
        </Text>

        <Text style={styles.paragraph}>
          I authorize the investigation of any or all statements contained in
          this application and authorize any person, school, current employer,
          past employers, and other organizations to provide information
          concerning my previous employment and other relevant information that
          may be useful in making a hiring decision. I release such people and
          organizations from any legal liability in making such statements.
        </Text>

        <Text style={styles.paragraph}>
          I have read, understand, and agree with the above statements.
        </Text>

        <View style={styles.signatureSection}>
          <View style={styles.signatureBlock}>
            <Image
              src={formData.applicantSignature}
              style={{ width: 200, height: 50 }}
            />
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Employee Signature</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text>{currentYear}</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Date</Text>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        Employment history will be verified. Providing false information may
        result in termination.
      </Text>
    </Page>

    {/* PAGE 6: Criminal History Disclosure */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE LLC</Text>
      </View>

      <Text style={styles.title}>
        DISCLOSURE OF CRIMINAL HISTORY CONSENT FOR BACKGROUND STUDY
      </Text>

      <View style={styles.section}>
        <Text style={styles.paragraph}>
          State of Minnesota requires all employees applying for positions of
          PCA/CFSS worker, RN or QP to have a full background study done by
          Minnesota Department of Human Services (Background Study Unit). By
          signing and filling out this form, you have agreed and given
          permission to SAMAH HOME HEALTH CARE, LLC to submit background study
          fingerprinting on your behalf with DHS Background Study Unit.
        </Text>

        <Text style={styles.paragraph}>
          Please answer all questions listed to the best of your knowledge.
          Explain in detail if any of your answers are marked "Yes".
        </Text>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Employee Full Legal Name <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>
                {formData.firstName} {formData.middleInitial}{" "}
                {formData.lastName}
              </Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Date of Birth <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.dobCriminal}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              SSN number <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.disclosureSSN}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Employee Address <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.employeeAddressCriminal}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Employee Place of Birth (Country){" "}
              <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.placeOfBirth}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Email <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.emailCriminal}</Text>
            </View>
          </View>
        </View>

        <View style={[styles.section, { backgroundColor: "#fff0f0", marginTop: 15 }]}>
          <View style={styles.questionRow}>
            <Text style={styles.label}>
              Have you been convicted of a felony?{" "}
              <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.checkboxGroup}>
              <View style={styles.checkboxItem}>
                <View
                  style={
                    formData.convictedFelony === "Yes"
                      ? styles.checkboxChecked
                      : styles.checkbox
                  }
                />
                <Text style={styles.label}>Yes</Text>
              </View>
              <View style={styles.checkboxItem}>
                <View
                  style={
                    formData.convictedFelony === "No"
                      ? styles.checkboxChecked
                      : styles.checkbox
                  }
                />
                <Text style={styles.label}>No</Text>
              </View>
            </View>
          </View>
          <View style={styles.questionRow}>
            <Text style={styles.label}>If yes, please explain in detail:</Text>
            <View style={styles.fieldFull}>
              <Text>{formData.felonyDetails}</Text>
            </View>
          </View>
        </View>

        <View style={[styles.section, { backgroundColor: "#fff0f0", marginTop: 10 }]}>
          <View style={styles.questionRow}>
            <Text style={styles.label}>
              Have you ever been charged or convicted of any crime other than a
              minor traffic violation? <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.checkboxGroup}>
              <View style={styles.checkboxItem}>
                <View
                  style={
                    formData.criminalHistory === "Yes"
                      ? styles.checkboxChecked
                      : styles.checkbox
                  }
                />
                <Text style={styles.label}>Yes</Text>
              </View>
              <View style={styles.checkboxItem}>
                <View
                  style={
                    formData.criminalHistory === "No"
                      ? styles.checkboxChecked
                      : styles.checkbox
                  }
                />
                <Text style={styles.label}>No</Text>
              </View>
            </View>
          </View>
          <View style={styles.questionRow}>
            <Text style={styles.label}>If yes, please explain in detail:</Text>
            <View style={styles.fieldFull}>
              <Text>{formData.criminalDetails}</Text>
            </View>
          </View>
        </View>

        <Text style={[styles.paragraph, { marginTop: 20 }]}>
          I hereby certify that the information given in this application is
          true and complete to the best of my knowledge. I authorize the
          investigation of all statements contained in this application for
          employment as may be necessary in arriving at an employment decision,
          including a criminal background study as required by the Minnesota
          Department of Human Services (DHS). I understand that false or
          misleading information given in my application or interview(s) may
          result in discharge.
        </Text>

        <View style={styles.signatureSection}>
          <View style={styles.signatureBlock}>
            <Image
              src={formData.applicantSignature}
              style={{ width: 200, height: 50 }}
            />
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Employee Signature</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text>{currentYear}</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Date</Text>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        Background checks are required for all healthcare positions. Honesty is
        essential for employment consideration.
      </Text>

      <View>
        <Text style={{ fontSize: 12, textAlign: "center" }}>
          PCA/CFSS Handbook
        </Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.title}>
          Care giver name _______{formData.clientName}______________________
        </Text>
        <Text style={styles.paragraph}>
          While SAMAH HOME HEALTH CARE believes wholeheartedly in the policies
          and procedures described here, they are not conditions of employment.
          SAMAH HOME HEALTH CARE reserves the right to apply or not apply, and
          to modify, revoke, suspend, terminate or change all plans, policies,
          or procedures described, in whole or in part, at any time without
          notice.
        </Text>
        <Text style={styles.paragraph}>
          The language used in this handbook is not intended to create, nor is
          it to be construed to constitute a contract between SAMAH HOME HEALTH
          CARE and any one of its employees. I further understand that
          employment may be terminated by SAMAH HOME HEALTH CARE at any time
          without prior notice. I also understand that the policies and
          procedures in this handbook may be changed at any time at the sole
          discretion on SAMAH HOME HEALTH CARE, with or without prior notice.
        </Text>
        <Text style={styles.paragraph}>
          All parties are responsible for complying with all rules and
          regulations related to the PCA / CFSS (caregivers) program. This
          includes, but is not limited to: state Vulnerable Adults Act, Data
          Privacy, PCA / CFSS (caregivers) regulations, including medication
          administration, and Department of Labor laws governing overtime, etc.
          It is a federal crime to provide false information on timecards and/or
          electronic time documentation for PCA / CFSS (caregivers) billings for
          medical assistance. Your signature (or telephone input when using the
          Dial n Documents) verifies the time and services are accurate and that
          the services were performed as specified in the PCA / CFSS
          (caregivers) Care Plan. Policy regarding Electronic Visit Verification
          (App / IVR Phone Call System), Policy QP Supervisory Visits, Care plan
          and County Assessments
        </Text>
        <Text style={styles.paragraph}>
          I have read, understand and agree to abide by all SAMAH HOME HEALTH
          CARE policies and procedures and the terms set forth therein
        </Text>

        <View style={styles.signatureSection}>
          <View style={styles.signatureBlock}>
            <Image
              src={formData.caregiverSignature}
              style={{ width: 150, height: 50 }}
            />
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Caregiver Signature</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text>{currentYear}</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Date</Text>
          </View>
        </View>
      </View>
    </Page>

    {/* PAGE 7: Direct Deposit Authorization */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE LLC</Text>
      </View>

      <Text style={styles.title}>DIRECT DEPOSIT AUTHORIZATION FORM</Text>

      <View style={styles.section}>
        <Text style={styles.paragraph}>
          I hereby authorize SAMAH HOME HEALTH CARE LLC to initiate credit
          entries and, if necessary, debit entries and adjustments for any
          credit entries in error to my account indicated below and the
          financial institution named below, to credit and/or debit the same to
          such account. This authority will remain in effect until SAMAH HOME
          HEALTH CARE LLC has received written notification from me of its
          termination in such time and in such manner as to afford the company
          and the financial institution a reasonable opportunity to act on it.
        </Text>

        <Text style={styles.sectionTitle}>Account Holder Information</Text>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              First Name <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.ddFirstName}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Middle Name</Text>
            <View style={styles.field}>
              <Text>{formData.ddMiddleName}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Last Name <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.ddLastName}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Date of Birth <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.ddDOB}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Last Four of SSN
            </Text>
            <View style={styles.field}>
              <Text>{formData.ddLastFourSSN}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Address <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.ddAddress}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              City/State <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.ddCityState}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Zip Code <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.ddZipCode}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Phone Number</Text>
            <View style={styles.field}>
              <Text>{formData.ddPhoneNumber}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Email</Text>
            <View style={styles.field}>
              <Text>{formData.ddEmail}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Bank Information</Text>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Bank Name <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.bankName}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Routing Number <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.bankRouting}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Account Number <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.bankAccount}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Bank Account Status<Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.ddAccount}</Text>
            </View>
          </View>
        </View>

        <Text
          style={[
            styles.paragraph,
            {
              marginTop: 15,
              backgroundColor: "#fff9e6",
              padding: 10,
              borderRadius: 4,
            },
          ]}
        >
          Please attach a voided check for account verification. If you close or
          change your account, you must notify the office immediately to avoid
          delays in receiving your paycheck.
        </Text>

        <View style={styles.signatureSection}>
          <View style={styles.signatureBlock}>
            <Image
              src={formData.applicantSignature}
              style={{ width: 200, height: 50 }}
            />
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Employee Signature</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text>{currentYear}</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Date</Text>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        Direct deposit information must be accurate. Incorrect information may
        delay payroll processing.
      </Text>
    </Page>

    {/* PAGE 8: Job Description & Duties Acknowledgment */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE LLC</Text>
      </View>

      <Text style={styles.title}>
        PERSONAL CARE ASSISTANT JOB DESCRIPTION & DUTIES ACKNOWLEDGMENT
      </Text>

      <View style={styles.section}>
        <Text style={styles.paragraph}>
          A Personal Care Assistant (PCA) provides services and supports to help
          a person with disabilities live independently in the community. PCAs
          assist with activities of daily living, health-related tasks, and
          other authorized services according to the individual care plan.
        </Text>

        <Text style={[styles.sectionTitle, { marginTop: 15 }]}>
          General Duties include but are not limited to:
        </Text>

        <View style={styles.dutiesList}>
          {[
            "Assisting with personal hygiene, dressing, grooming, bathing, and toileting.",
            "Helping with meal preparation, feeding, and special dietary needs.",
            "Performing basic household tasks related to the client's care.",
            "Assisting with mobility, transfers, and use of adaptive equipment.",
            "Providing reminders for medication (but not administering medication).",
            "Observing, documenting, and reporting any changes in client's condition to the QP or RN.",
            "Maintaining confidentiality and following HIPAA guidelines.",
            "Complying with all agency policies and DHS regulations.",
            "Providing transportation for medical appointments and community activities as authorized.",
            "Documenting services provided according to agency requirements.",
          ].map((duty, i) => (
            <View style={styles.dutyItem} key={i}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.listText}>{duty}</Text>
            </View>
          ))}
        </View>

        <Text style={[styles.paragraph, { marginTop: 15 }]}>
          I have read and understand the job description and agree to perform
          the duties described above to the best of my ability. I understand
          that failure to comply with these duties may result in disciplinary
          action up to and including termination.
        </Text>

        <View style={styles.signatureSection}>
          <View style={styles.signatureBlock}>
            <Image
              src={formData.caregiverSignature}
              style={{ width: 200, height: 50 }}
            />
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Employee Signature</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text>{currentYear}</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Date</Text>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        Job duties may vary based on client needs and care plans. Additional
        training may be required.
      </Text>
    </Page>

    {/* PAGE 9: Employee Orientation Acknowledgment */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE LLC</Text>
      </View>

      <Text style={styles.title}>EMPLOYEE ORIENTATION ACKNOWLEDGMENT</Text>

      <View style={styles.section}>
        <Text style={styles.paragraph}>
          I acknowledge that I have received orientation training as required by
          the Minnesota Department of Human Services. Topics covered include:
        </Text>

        <View style={styles.dutiesList}>
          {[
            "Company policies and procedures",
            "Emergency procedures and safety protocols",
            "Timecard completion and payroll processes",
            "HIPAA compliance and confidentiality requirements",
            "Client rights and responsibilities",
            "Abuse prevention and reporting procedures",
            "Documentation standards and requirements",
            "Infection control practices",
            "Cultural competency and diversity training",
            "Professional boundaries and ethics",
          ].map((topic, i) => (
            <View style={styles.dutyItem} key={i}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.listText}>{topic}</Text>
            </View>
          ))}
        </View>

        <Text style={[styles.paragraph, { marginTop: 15 }]}>
          I understand that I am responsible for following all company rules and
          maintaining confidentiality of all client and agency information. I
          agree to comply with all state and federal regulations governing home
          health care services.
        </Text>
        <Text>
          Caregiver Name: _______{formData.clientName}______________________
        </Text>

        <View style={styles.signatureSection}>
          <View style={styles.signatureBlock}>
            <Image
              src={formData.caregiverSignature}
              style={{ width: 200, height: 50 }}
            />
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Caregiver Signature</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text>{currentYear}</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Date</Text>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        Ongoing training may be required. Employees must maintain current
        certifications as required by position.
      </Text>
    </Page>

    {/* PAGE 10: Employee Orientation Acknowledgment (Continued) */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE LLC</Text>
      </View>

      <Text style={styles.title}>EMPLOYEE ORIENTATION ACKNOWLEDGMENT (Continued)</Text>

      <View style={styles.section}>
        <Text style={styles.paragraph}>
          I acknowledge that I have received orientation training as required by
          the Minnesota Department of Human Services. Topics covered include:
        </Text>

        <View style={styles.dutiesList}>
          {[
            "EMPLOYEE HANDBOOK / POLICY AND PROCEDURE",
            "ELECTRONIC VISIT VERIFICATION App and IVR Call System FRAUD (Timecards)",
            "FOR PATIENT RIGHTS",
            "AMBULATION, CRUTCHES, WHEELCHAIR, WALKER",
            "GROOMING (NAIL CARE, HAIR CARE, SKIN CARE)",
            "TOILETING",
            "BATHING (BED, SHOWER, SPONGE, TUB) BED LINEN CHANGE, OCCUPIED/UNOCCUPIED",
            "MOBILITY (Moving member in Bed, Couch, Chair, Recliner, etc.)",
            "BODY MECHANICS (LIFTING AND TURNING)",
            "DRESSING ASSISTING, CLOTHES",
            "EATING (ASSISTANCE WITH EATING, TRAY SET UP)",
            "GROOMING, HAND HYGIENE, INFECTION CONTROL",
            "MAINTENANCE OF CLEAN AND SAFE ENVIRONMENT",
          ].map((topic, i) => (
            <View style={styles.dutyItem} key={i}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.listText}>{topic}</Text>
            </View>
          ))}
        </View>

        <Text style={[styles.paragraph, { marginTop: 15 }]}>
          I understand that I am responsible for following all company rules and
          maintaining confidentiality of all client and agency information. I
          agree to comply with all state and federal regulations governing home
          health care services.
        </Text>
        <Text>
          Caregiver Name: _______{formData.clientName}______________________
        </Text>

        <View style={styles.signatureSection}>
          <View style={styles.signatureBlock}>
            <Image
              src={formData.caregiverSignature}
              style={{ width: 200, height: 50 }}
            />
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Caregiver Signature</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text>{currentYear}</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Date</Text>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        Ongoing training may be required. Employees must maintain current
        certifications as required by position.
      </Text>
    </Page>

    {/* PAGE 11: Form I-9 Employment Eligibility Verification */}
<Page size="A4" style={styles.page}>
  <View style={styles.header}>
    <Text style={styles.companyName}>SAMAH HOME HEALTH CARE LLC</Text>
  </View>

  <Text style={styles.title}>
    FORM I-9 - EMPLOYMENT ELIGIBILITY VERIFICATION
  </Text>
  <Text style={{ textAlign: "center", fontSize: 8, marginBottom: 15 }}>
    Department of Homeland Security · U.S. Citizenship and Immigration Services
  </Text>

  <View style={styles.section}>
    <Text style={styles.sectionTitle}>
      Section 1. Employee Information and Attestation
    </Text>
    <Text style={[styles.note, { marginBottom: 10 }]}>
      Employees must complete and sign Section 1 of Form I-9 no later than the first day of employment
    </Text>

    <View style={styles.questionRow}>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>Last Name (Family Name)</Text>
        <View style={styles.field}>
          <Text>{formData.i9LastName || ""}</Text>
        </View>
      </View>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>First Name (Given Name)</Text>
        <View style={styles.field}>
          <Text>{formData.i9FirstName || ""}</Text>
        </View>
      </View>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>Middle Initial</Text>
        <View style={styles.field}>
          <Text>{formData.i9MiddleInitial || ""}</Text>
        </View>
      </View>
    </View>

    <View style={styles.questionRow}>
      <View style={styles.fieldFull}>
        <Text style={styles.label}>Other Last Names Used (if any)</Text>
        <View style={styles.field}>
          <Text>{formData.i9OtherNames || ""}</Text>
        </View>
      </View>
    </View>

    <View style={styles.questionRow}>
      <View style={styles.fieldFull}>
        <Text style={styles.label}>Address (Street Number and Name)</Text>
        <View style={styles.field}>
          <Text>{formData.i9Address || ""}</Text>
        </View>
      </View>
    </View>

    <View style={styles.questionRow}>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>Apt. Number (if any)</Text>
        <View style={styles.field}>
          <Text>{formData.i9AptNumber || ""}</Text>
        </View>
      </View>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>City or Town</Text>
        <View style={styles.field}>
          <Text>{formData.i9City || ""}</Text>
        </View>
      </View>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>State</Text>
        <View style={styles.field}>
          <Text>{formData.i9State || ""}</Text>
        </View>
      </View>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>ZIP Code</Text>
        <View style={styles.field}>
          <Text>{formData.i9Zip || ""}</Text>
        </View>
      </View>
    </View>

    <View style={styles.questionRow}>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>Date of Birth (mm/dd/yyyy)</Text>
        <View style={styles.field}>
          <Text>{formData.i9DOB || ""}</Text>
        </View>
      </View>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>U.S. Social Security Number</Text>
        <View style={styles.field}>
          <Text>{formData.i9SSN || ""}</Text>
        </View>
      </View>
    </View>

    <View style={styles.questionRow}>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>Employee's Email Address</Text>
        <View style={styles.field}>
          <Text>{formData.i9Email || ""}</Text>
        </View>
      </View>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>Employee's Telephone Number</Text>
        <View style={styles.field}>
          <Text>{formData.i9Phone || ""}</Text>
        </View>
      </View>
    </View>

    <Text style={[styles.label, { marginTop: 15, marginBottom: 8 }]}>
      I am aware that federal law provides for imprisonment and/or fines for false statements, or the use of false documents, in connection with the completion of this form. I attest, under penalty of perjury, that this information, including my selection of the box attesting to my citizenship or immigration status, is true and correct.
    </Text>

    <View style={[styles.questionRow, { marginTop: 10 }]}>
      <View style={styles.checkboxGroup}>
        <View style={styles.checkboxItem}>
          <View style={formData.i9CitizenStatus === "1. A citizen of the United States" ? styles.checkboxChecked : styles.checkbox} />
          <Text style={styles.checkboxLabel}>1. A citizen of the United States</Text>
        </View>
        <View style={styles.checkboxItem}>
          <View style={formData.i9CitizenStatus === "2. A noncitizen national of the United States" ? styles.checkboxChecked : styles.checkbox} />
          <Text style={styles.checkboxLabel}>2. A noncitizen national of the United States</Text>
        </View>
      </View>
    </View>

    <View style={styles.questionRow}>
      <View style={styles.checkboxItem}>
        <View style={formData.i9CitizenStatus === "3. A lawful permanent resident" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>3. A lawful permanent resident (Alien Registration Number/USCIS Number):</Text>
      </View>
      <View style={[styles.field, { width: 200, marginLeft: 25 }]}>
        <Text>{formData.i9AlienNumber || ""}</Text>
      </View>
    </View>

    <View style={styles.questionRow}>
      <View style={styles.checkboxItem}>
        <View style={formData.i9CitizenStatus === "4. An alien authorized to work until" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>4. An alien authorized to work until (expiration date, if applicable, mm/dd/yyyy):</Text>
      </View>
      <View style={[styles.field, { width: 120, marginLeft: 25 }]}>
        <Text>{formData.i9WorkAuthExpiration || ""}</Text>
      </View>
    </View>

    {(formData.i9CitizenStatus === "3. A lawful permanent resident" || formData.i9CitizenStatus === "4. An alien authorized to work until") && (
      <View style={{ marginLeft: 30, marginTop: 10 }}>
        <View style={styles.questionRow}>
          <Text style={styles.label}>Alien Registration Number/USCIS Number:</Text>
          <View style={[styles.field, { width: 200 }]}>
            <Text>{formData.i9AlienNumber || ""}</Text>
          </View>
        </View>
        <View style={styles.questionRow}>
          <Text style={styles.label}>Form I-94 Admission Number:</Text>
          <View style={[styles.field, { width: 200 }]}>
            <Text>{formData.i9AdmissionNumber || ""}</Text>
          </View>
        </View>
        <View style={styles.questionRow}>
          <Text style={styles.label}>Foreign Passport Number:</Text>
          <View style={[styles.field, { width: 200 }]}>
            <Text>{formData.i9ForeignPassportNumber || ""}</Text>
          </View>
        </View>
        <View style={styles.questionRow}>
          <Text style={styles.label}>Country of Issuance:</Text>
          <View style={[styles.field, { width: 200 }]}>
            <Text>{formData.i9CountryOfIssuance || ""}</Text>
          </View>
        </View>
      </View>
    )}

    <View style={styles.signatureSection}>
      <View style={styles.signatureBlock}>
        <Image src={formData.applicantSignature} style={{ width: 200, height: 50 }} />
        <View style={styles.signatureLine} />
        <Text style={styles.signatureLabel}>Signature of Employee</Text>
      </View>
      <View style={styles.signatureBlock}>
        <Text>{formData.i9Date || currentYear}</Text>
        <View style={styles.signatureLine} />
        <Text style={styles.signatureLabel}>Date (mm/dd/yyyy)</Text>
      </View>
    </View>

    <Text style={[styles.note, { marginTop: 10 }]}>
      If a preparer and/or translator assisted you in completing Section 1, that person MUST complete the Preparer and/or Translator Certification.
    </Text>
  </View>
</Page>

{/* PAGE 12: I-9 Section 2 - Employer Review */}
<Page size="A4" style={styles.page}>
  <View style={styles.header}>
    <Text style={styles.companyName}>SAMAH HOME HEALTH CARE LLC</Text>
  </View>

  <Text style={styles.title}>FORM I-9 - SECTION 2</Text>
  <Text style={{ textAlign: "center", fontSize: 8, marginBottom: 15 }}>
    Employer or Authorized Representative Review and Verification
  </Text>

  <View style={styles.section}>
    <Text style={styles.sectionTitle}>
      Section 2. Employer or Authorized Representative Review
    </Text>
    <Text style={[styles.note, { marginBottom: 10 }]}>
      Employers must examine one document from List A OR a combination of one document from List B and one document from List C.
    </Text>

    {/* List A */}
    <View style={[styles.infoSection, { backgroundColor: "#f0f9ff" }]}>
      <Text style={[styles.subTitle, { color: "#2563eb" }]}>List A — Documents that Establish Both Identity and Employment Authorization</Text>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListADocument === "U.S. Passport or U.S. Passport Card" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>U.S. Passport or U.S. Passport Card</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListADocument === "Permanent Resident Card or Alien Registration Receipt Card" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>Permanent Resident Card or Alien Registration Receipt Card (Form I-551)</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListADocument === "Foreign passport with Form I-94" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>Foreign passport with Form I-94 or Form I-94A</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListADocument === "Employment Authorization Document (EAD)" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>Employment Authorization Document (EAD) (Form I-766)</Text>
      </View>

      {formData.i9ListADocument && (
        <View style={{ marginTop: 10 }}>
          <Text style={styles.label}>Document Number: {formData.i9ListADocumentNumber || ""}</Text>
          <Text style={styles.label}>Expiration Date: {formData.i9ListAExpirationDate || ""}</Text>
        </View>
      )}
    </View>

    {/* List B */}
    <View style={[styles.infoSection, { backgroundColor: "#fefce8" }]}>
      <Text style={[styles.subTitle, { color: "#ca8a04" }]}>List B — Documents that Establish Identity</Text>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListBDocument === "Driver's license issued by a U.S. state" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>Driver's license issued by a U.S. state or territory</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListBDocument === "State-issued ID card" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>State-issued ID card</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListBDocument === "School ID with photo" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>School ID with photograph</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListBDocument === "Voter's registration card" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>Voter's registration card</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListBDocument === "U.S. Military card" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>U.S. Military card or draft record</Text>
      </View>

      {formData.i9ListBDocument && (
        <View style={{ marginTop: 10 }}>
          <Text style={styles.label}>Document Number: {formData.i9ListBDocumentNumber || ""}</Text>
        </View>
      )}
    </View>

    {/* List C */}
    <View style={[styles.infoSection, { backgroundColor: "#dcfce7" }]}>
      <Text style={[styles.subTitle, { color: "#16a34a" }]}>List C — Documents that Establish Employment Authorization</Text>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListCDocument === "Social Security Account Number card" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>Social Security Account Number card (other than card stating \"not valid for employment\")</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListCDocument === "Birth certificate" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>Birth certificate</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListCDocument === "Employment Authorization Document" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>Employment Authorization Document (EAD)</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListCDocument === "Native American tribal document" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>Native American tribal document</Text>
      </View>
      <View style={styles.checkboxItem}>
        <View style={formData.i9ListCDocument === "U.S. Citizen ID card" ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>U.S. Citizen ID card (Form I-197)</Text>
      </View>

      {formData.i9ListCDocument && (
        <View style={{ marginTop: 10 }}>
          <Text style={styles.label}>Document Number: {formData.i9ListCDocumentNumber || ""}</Text>
        </View>
      )}
    </View>

    <View style={[styles.questionRow, { marginTop: 15 }]}>
      <View style={styles.checkboxItem}>
        <View style={formData.i9AlternativeProcedure ? styles.checkboxChecked : styles.checkbox} />
        <Text style={styles.checkboxLabel}>Check here if you used an alternative procedure authorized by DHS to examine documents.</Text>
      </View>
    </View>

    <Text style={[styles.paragraph, { marginTop: 15 }]}>
      I attest, under penalty of perjury, that (1) I have examined the documentation presented by the above-named employee, 
      (2) the above-listed documentation appears to be genuine and to relate to the employee named, and (3) to the best of my knowledge, 
      the employee is authorized to work in the United States.
    </Text>

    <View style={styles.questionRow}>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>First Day of Employment (mm/dd/yyyy)</Text>
        <View style={styles.field}>
          <Text>{formData.i9FirstDayOfEmployment || ""}</Text>
        </View>
      </View>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>Today's Date (mm/dd/yyyy)</Text>
        <View style={styles.field}>
          <Text>{formData.i9EmployerDate || currentYear}</Text>
        </View>
      </View>
    </View>

    <View style={styles.questionRow}>
      <View style={styles.fieldFull}>
        <Text style={styles.label}>Employer's Business or Organization Name</Text>
        <View style={styles.field}>
          <Text>{formData.i9EmployerName || "SAMAH HOME HEALTH CARE LLC"}</Text>
        </View>
      </View>
    </View>

    <View style={styles.questionRow}>
      <View style={styles.fieldFull}>
        <Text style={styles.label}>Employer's Business or Organization Address, City, State, ZIP Code</Text>
        <View style={styles.field}>
          <Text>{formData.i9EmployerAddress || "330 SW Fourth Street Suite #9B, Willmar MN 56201"}</Text>
        </View>
      </View>
    </View>

    <View style={styles.signatureSection}>
      <View style={styles.signatureBlock}>
        <Image src={formData.hrSignature} style={{ width: 200, height: 50 }} />
        <View style={styles.signatureLine} />
        <Text style={styles.signatureLabel}>Signature of Employer or Authorized Representative</Text>
      </View>
      <View style={styles.signatureBlock}>
        <Text>{formData.i9EmployerTitle || ""}</Text>
        <View style={styles.signatureLine} />
        <Text style={styles.signatureLabel}>Title of Employer or Authorized Representative</Text>
      </View>
    </View>
  </View>
</Page>

{/* PAGE 13: I-9 Section 3 - Reverification and Rehire */}
<Page size="A4" style={styles.page}>
  <View style={styles.header}>
    <Text style={styles.companyName}>SAMAH HOME HEALTH CARE LLC</Text>
  </View>

  <Text style={styles.title}>FORM I-9 - SECTION 3</Text>
  <Text style={{ textAlign: "center", fontSize: 8, marginBottom: 15 }}>
    Reverification and Rehire (if applicable)
  </Text>

  <View style={styles.section}>
    <Text style={styles.sectionTitle}>
      Section 3. Reverification and Rehire
    </Text>
    <Text style={[styles.note, { marginBottom: 10 }]}>
      Complete this section if the employee's work authorization has expired or if rehiring within 3 years of original I-9.
    </Text>

    <Text style={[styles.paragraph, { marginBottom: 10 }]}>
      If the employee's previous grant of employment authorization has expired, reverification is required to confirm current employment authorization. 
      Reverification is also required when rehiring an employee within 3 years of the date of the original I-9.
    </Text>

    <View style={styles.questionRow}>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>New Work Authorization Expiration Date (if any)</Text>
        <View style={styles.field}>
          <Text>{formData.i9NewExpirationDate || ""}</Text>
        </View>
      </View>
    </View>

    <View style={styles.questionRow}>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>Document Title</Text>
        <View style={styles.field}>
          <Text>{formData.i9ReverifyDocumentTitle || ""}</Text>
        </View>
      </View>
      <View style={styles.questionGroup}>
        <Text style={styles.label}>Document Number</Text>
        <View style={styles.field}>
          <Text>{formData.i9ReverifyDocumentNumber || ""}</Text>
        </View>
      </View>
    </View>

    <View style={styles.signatureSection}>
      <View style={styles.signatureBlock}>
        <Text>{formData.i9ReverifySignature || ""}</Text>
        <View style={styles.signatureLine} />
        <Text style={styles.signatureLabel}>Signature of Employer or Authorized Representative</Text>
      </View>
      <View style={styles.signatureBlock}>
        <Text>{formData.i9ReverifyDate || ""}</Text>
        <View style={styles.signatureLine} />
        <Text style={styles.signatureLabel}>Date (mm/dd/yyyy)</Text>
      </View>
    </View>

    <View style={[styles.questionRow, { marginTop: 15 }]}>
      <View style={styles.fieldFull}>
        <Text style={styles.label}>Name of Employer or Authorized Representative (Print)</Text>
        <View style={styles.field}>
          <Text>{formData.i9EmployerTitle || ""}</Text>
        </View>
      </View>
    </View>

    <View style={[styles.infoSection, { marginTop: 20, backgroundColor: "#fef2f2" }]}>
      <Text style={styles.paragraph}>
        <Text style={{ fontWeight: "bold" }}>IMPORTANT:</Text> Employers must retain completed Form I-9 for three years after the date of hire or one year after employment ends, whichever is later. 
        The form must be available for inspection by authorized DHS, DOL, and DOJ officers.
      </Text>
    </View>
  </View>

  <Text style={styles.disclaimer}>
    Original documents must be presented for verification. Photocopies are not acceptable unless authorized by DHS.
  </Text>
</Page>
    {/* PAGE 12: IRS Form W-4 */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE LLC</Text>
      </View>

      <Text style={styles.title}>
        IRS FORM W-4 - EMPLOYEE'S WITHHOLDING CERTIFICATE
      </Text>

      <View style={styles.section}>
        <Text style={styles.paragraph}>
          Complete this form so your employer can withhold the correct federal
          income tax from your pay. Your withholding is subject to review by the
          IRS.
        </Text>

        <Text style={styles.sectionTitle}>
          Step 1: Enter Personal Information
        </Text>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              First Name and Middle Initial{" "}
              <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.w4FirstName}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>
              Last Name <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.field}>
              <Text>{formData.w4LastName}</Text>
            </View>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Social Security Number <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.w4SSN}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            Address <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.w4Address}</Text>
          </View>
        </View>

        <View style={styles.questionRow}>
          <Text style={styles.label}>
            City or Town, State, and ZIP Code{" "}
            <Text style={styles.required}>*</Text>
          </Text>
          <View style={styles.fieldFull}>
            <Text>{formData.w4CityStateZip}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>
          Step 2: Multiple Jobs or Spouse Works
        </Text>
        <View style={styles.questionRow}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <View
              style={
                formData.w4MultipleJobs
                  ? styles.checkboxChecked
                  : styles.checkbox
              }
            />
            <Text
              style={[styles.label, { marginLeft: 5, fontWeight: "normal" }]}
            >
              Check this box if you hold more than one job or are married filing
              jointly with a working spouse
            </Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Step 3: Claim Dependents</Text>
        <Text style={styles.label}>
          If your total income will be $200,000 or less ($400,000 or less if
          married filing jointly):
        </Text>
        <View style={styles.fieldFull}>
          <Text>{formData.w4Dependents}</Text>
        </View>

        <Text style={styles.sectionTitle}>
          Step 4: Other Adjustments (optional)
        </Text>

        <View style={styles.questionRow}>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Other income (not from jobs)</Text>
            <View style={styles.field}>
              <Text>{formData.w4OtherIncome}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Deductions</Text>
            <View style={styles.field}>
              <Text>{formData.w4Deductions}</Text>
            </View>
          </View>
          <View style={styles.questionGroup}>
            <Text style={styles.label}>Extra withholding each pay period</Text>
            <View style={styles.field}>
              <Text>{formData.w4ExtraWithholding}</Text>
            </View>
          </View>
        </View>

        <View style={styles.signatureSection}>
          <View style={styles.signatureBlock}>
            <Image
              src={formData.applicantSignature}
              style={{ width: 200, height: 50 }}
            />
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Employee Signature</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text>{currentYear}</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Date</Text>
          </View>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        Consult a tax professional for guidance on withholding allowances.
        Updates to W-4 may be made at any time.
      </Text>
    </Page>

    {/* PAGE 13: Flyte MEC Plan Participation Agreement */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.companyName}>SAMAH HOME HEALTH CARE LLC</Text>
      </View>

      <Text style={styles.title}>FLYTE MEC PLAN PARTICIPATION AGREEMENT</Text>

      <View style={styles.section}>
        <Text>
          Minimum Essential Coverage (MEC) Plan Enrollment — Complete this form
          to communicate your participation, eligibility, and covered
          individuals.
        </Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Member Information</Text>
        <View style={styles.row}>
          <View style={styles.fieldHalf}>
            <Text>First Name: {formData.flyteFirstName}</Text>
          </View>
          <View style={styles.fieldHalf}>
            <Text>Middle Initial: {formData.flyteMiddleInitial}</Text>
          </View>
        </View>
        <View style={styles.row}>
          <View style={styles.fieldHalf}>
            <Text>Last Name: {formData.flyteLastName}</Text>
          </View>
          <View style={styles.fieldHalf}>
            <Text>SSN: {formData.flyteSSN}</Text>
          </View>
        </View>
        <View style={styles.row}>
          <View style={styles.fieldHalf}>
            <Text>Address: {formData.flyteAddress}</Text>
          </View>
          <View style={styles.fieldHalf}>
            <Text>City: {formData.flyteCity}</Text>
          </View>
        </View>
        <View style={styles.row}>
          <View style={styles.fieldHalf}>
            <Text>State: {formData.flyteState}</Text>
          </View>
          <View style={styles.fieldHalf}>
            <Text>ZIP: {formData.flyteZip}</Text>
          </View>
        </View>
        <View style={styles.row}>
          <View style={styles.fieldHalf}>
            <Text>Company Name: {formData.flyteCompanyName}</Text>
          </View>
          <View style={styles.fieldHalf}>
            <Text>Email: {formData.flyteMemberEmail}</Text>
          </View>
        </View>
        <Text>Coverage Level: {formData.flyteCoverageLevel}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Type of Enrollment</Text>
        <Text>{formData.flyteEnrollmentType}</Text>

        <View style={[styles.row, { marginTop: 6 }]}>
          <View style={styles.fieldHalf}>
            <Text>Hire Date: {formData.flyteHireDate}</Text>
          </View>
          <View style={styles.fieldHalf}>
            <Text>Effective Date: {formData.flyteEffectiveDate}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Covered Individuals</Text>

        <View style={styles.tableHeader}>
          {[
            "Name",
            "SSN",
            "DOB",
            "Gender",
            "Relationship",
            "Insurance Carrier",
          ].map((col, i) => (
            <Text key={i} style={styles.cell}>
              {col}
            </Text>
          ))}
        </View>

        {[...Array(4)].map((_, i) => (
          <View key={i} style={styles.tableRow}>
            <Text style={styles.cell}>{formData[`flyteCoveredName${i}`]}</Text>
            <Text style={styles.cell}>{formData[`flyteCoveredSSN${i}`]}</Text>
            <Text style={styles.cell}>{formData[`flyteCoveredDOB${i}`]}</Text>
            <Text style={styles.cell}>
              {formData[`flyteCoveredGender${i}`]}
            </Text>
            <Text style={styles.cell}>
              {formData[`flyteCoveredRelationship${i}`]}
            </Text>
            <Text style={styles.cell}>
              {formData[`flyteCoveredCarrier${i}`]}
            </Text>
          </View>
        ))}
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Participation Waiver</Text>
        {formData.flyteWaiver ? (
          <Text>
            ☑ I do <Text style={{ fontWeight: "bold" }}>NOT</Text> wish to
            participate in this tax-free, employer-sponsored plan.
          </Text>
        ) : (
          <Text>☐ I wish to participate in the plan.</Text>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Certification & Acknowledgment</Text>
        <Text>
          I understand that my coverage can only be changed during the open
          enrollment period of the MEC or if I have a Qualified Change affecting
          my eligibility or the eligibility of my covered family members. I was
          offered the ability to opt out of the plan and have checked the box
          above if I chose to do so.
        </Text>
      </View>

      <View style={styles.signatureSection}>
        <View style={styles.signatureBlock}>
          {formData.hrSignature && (
            <Image
              src={formData.hrSignature}
              style={{ width: 200, height: 50 }}
            />
          )}
          <View style={styles.signatureLine} />
          <Text style={styles.signatureLabel}>HR Signature</Text>
        </View>
        <View style={styles.signatureBlock}>
          {formData.applicantSignature && (
            <Image
              src={formData.applicantSignature}
              style={{ width: 200, height: 50 }}
            />
          )}
          <View style={styles.signatureLine} />
          <Text style={styles.signatureLabel}>Employee Signature</Text>
        </View>
        <View style={styles.signatureBlock}>
          <Text>{currentYear}</Text>
          <View style={styles.signatureLine} />
          <Text style={styles.signatureLabel}>Date</Text>
        </View>
      </View>

      <Text style={styles.disclaimer}>
        Health insurance elections are subject to eligibility requirements and
        plan availability.
      </Text>
    </Page>
  </Document>
);

export default PCAApplicationPDF;